/**
 * Analıq Məzuniyyəti və Hamiləlik Müavinəti Kalkulyatoru (2026)
 * Hüquqi Baza:
 * - AR Əmək Məcəlləsi, Maddə 125 (125.1, 125.2, 125.3)
 * - AR Nazirlər Kabinetinin 189 nömrəli Qərarı ilə təsdiq edilmiş Qayda
 *   (Bölmə 3: Bəndlər 3.1–3.5, Bölmə 2: Bəndlər 2.7, 2.8, 2.9, 2.13)
 * - AR Vergi Məcəlləsi, Maddə 102.1.4 (Gəlir vergisindən azadolma)
 * - "Sosial sığorta haqqında" AR Qanunu (6 ay sosial sığorta stajı şərti)
 * - 2026 göstəriciləri: Minimum əmək pensiyası = 320 AZN, Aylıq maksimal hədd = 25 × 320 = 8 000 AZN (Bənd 3.3), Minimum aylıq əməkhaqqı = 400 AZN (Bənd 2.9)
 */

export const MIN_PENSION_2026 = 320;
export const MAX_MONTHLY_LIMIT_2026 = 320 * 25; // 8 000 AZN (Bənd 3.3)
export const MIN_SALARY_2026 = 400; // Minimum aylıq əməkhaqqı (Bənd 2.9)

/**
 * 1. Doğuş kateqoriyasına uyğun təqvim günlərinin sayı (TGS)
 * AR Əmək Məcəlləsi, Maddə 125
 */
export function getLeaveDurationDays(activityType = 'general', birthType = 'normal') {
  if (activityType === 'agriculture') {
    switch (birthType) {
      case 'complicated':
        return 156;
      case 'multiple':
        return 180;
      case 'normal':
      default:
        return 140;
    }
  }

  // Ümumi fəaliyyət
  switch (birthType) {
    case 'complicated':
    case 'multiple':
      return 140;
    case 'normal':
    default:
      return 126;
  }
}

/**
 * 2. Məzuniyyətin bitmə tarixinin hesablanması (Faktiki doğum tarixi nəzərə alınmaqla)
 * AR Əmək Məcəlləsi, Maddə 125.3
 */
export function calculateLeaveDates(startDateStr, totalDays, actualBirthDateStr = null) {
  if (!startDateStr) return null;

  const start = new Date(startDateStr + 'T00:00:00');
  let effectiveDays = totalDays;
  let delayDays = 0;

  if (actualBirthDateStr) {
    const actual = new Date(actualBirthDateStr + 'T00:00:00');
    // Doğuşaqədərki standart dövr 70 gündür
    const expectedBirth = new Date(start);
    expectedBirth.setDate(expectedBirth.getDate() + 70);

    if (actual > expectedBirth) {
      const diffMs = actual.getTime() - expectedBirth.getTime();
      delayDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
      effectiveDays += delayDays;
    }
  }

  const end = new Date(start);
  end.setDate(end.getDate() + effectiveDays - 1);

  const formatDate = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  return {
    startDate: startDateStr,
    endDate: formatDate(end),
    totalDays: effectiveDays,
    standardDays: totalDays,
    delayDays,
  };
}

/**
 * 3. Məzuniyyətin başladığı aydan əvvəlki ardıcıl 4 tam rübün müəyyən edilməsi
 * Qaydanın 2.7 və 3.4-cü bəndləri
 * Faktiki təqvim günlərinin cəmi dəqiq hesablanır (365 və ya 366 gün).
 */
export function getPrecedingFourQuarters(startDateStr) {
  if (!startDateStr) return { quarters: [], basePeriodStart: '', basePeriodEnd: '', totalCalendarDays: 365 };

  const start = new Date(startDateStr + 'T00:00:00');
  const eventYear = start.getFullYear();
  const eventMonth = start.getMonth(); // 0-based: 0 = Jan, 8 = Sep

  // Hadisənin düşdüyü rüb (0 = Q1, 1 = Q2, 2 = Q3, 3 = Q4)
  const eventQuarter = Math.floor(eventMonth / 3);

  // Əvvəlki 4 rüb (xronoloji ardıcıllıqla)
  const quarters = [];
  let curQ = eventQuarter;
  let curY = eventYear;

  for (let i = 0; i < 4; i++) {
    curQ -= 1;
    if (curQ < 0) {
      curQ = 3;
      curY -= 1;
    }

    const qNum = curQ + 1; // 1, 2, 3, 4
    const startM = curQ * 3;
    const endM = startM + 2;

    const qStartDate = new Date(curY, startM, 1);
    const qEndDate = new Date(curY, endM + 1, 0); // Ayın son günü

    const diffDays = Math.round((qEndDate.getTime() - qStartDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    const romanQ = ['I', 'II', 'III', 'IV'][curQ];

    const formatDate = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };

    quarters.unshift({
      key: `Q${qNum}_${curY}`,
      label: `${romanQ} rüb ${curY}`,
      year: curY,
      quarter: qNum,
      startDate: formatDate(qStartDate),
      endDate: formatDate(qEndDate),
      calendarDays: diffDays,
    });
  }

  const basePeriodStart = quarters[0].startDate;
  const basePeriodEnd = quarters[quarters.length - 1].endDate;
  const totalCalendarDays = quarters.reduce((sum, q) => sum + q.calendarDays, 0);

  return {
    quarters,
    basePeriodStart,
    basePeriodEnd,
    totalCalendarDays, // Faktiki ÜTG (365 və ya 366 gün)
  };
}

/**
 * 4. Çıxılan təqvim günlərinin (ÇTG) hesablanması (İnterval kəsişməsi ilə)
 * Qaydanın 2.13-cü bəndi üzrə yalnız normativ əsaslı dövrlər
 */
export function calculateExcludedDays(excludedPeriods = [], basePeriodStartStr, basePeriodEndStr) {
  if (!basePeriodStartStr || !basePeriodEndStr || !excludedPeriods.length) {
    return { excludedDays: 0, items: [] };
  }

  const baseStart = new Date(basePeriodStartStr + 'T00:00:00').getTime();
  const baseEnd = new Date(basePeriodEndStr + 'T00:00:00').getTime();

  let totalExcluded = 0;
  const processedItems = [];

  excludedPeriods.forEach((period, idx) => {
    if (!period.startDate || !period.endDate) return;

    const pStart = new Date(period.startDate + 'T00:00:00').getTime();
    const pEnd = new Date(period.endDate + 'T00:00:00').getTime();

    // Kəsişmə
    const intStart = Math.max(pStart, baseStart);
    const intEnd = Math.min(pEnd, baseEnd);

    if (intStart <= intEnd) {
      const days = Math.round((intEnd - intStart) / (1000 * 60 * 60 * 24)) + 1;
      totalExcluded += days;
      processedItems.push({
        id: idx,
        type: period.type || 'xestelik',
        rawDays: Math.round((pEnd - pStart) / (1000 * 60 * 60 * 24)) + 1,
        interceptedDays: days,
        startDate: period.startDate,
        endDate: period.endDate,
      });
    }
  });

  return {
    excludedDays: totalExcluded,
    items: processedItems,
  };
}

/**
 * 5. Əsas Hamiləlik və Doğum Müavinətinin Hesablanması
 * Düsturlar:
 * MM = TGS × OQM
 * OQM = ÜQM / BTG
 * BTG = ÜTG - ÇTG
 * ÜQM = MDSS × 4 (Bənd 2.7)
 *
 * Hüquqi Tələb (Bənd 3.3):
 * 8 000 AZN tavanı üçün heç bir sün'i 8000 / (365/12) gündəlik bölən tətbiq olunmur və
 * müavinət avtomatik azaldılmır. finalBenefit birbaşa TGS × OQM olaraq qalır,
 * 8 000 AZN isə aylıq hüquqi xəbərdarlıq və meyar kimi təqdim edilir.
 */
export function calculateMaternityBenefit2026({
  startDate,
  actualBirthDate = null,
  activityType = 'general', // 'general' | 'agriculture'
  birthType = 'normal', // 'normal' | 'complicated' | 'multiple'
  seniorityMonths = 12, // Staj (ay)
  hadEmployment = true, // Əvvəlki 4 rübdə əmək fəaliyyəti olubmu? (Bənd 2.9)
  quarterlyMDSS = {}, // { 'Q3_2025': 250, ... }
  excludedPeriods = [],
}) {
  // 1. Məzuniyyət müddəti və tarixləri (TGS)
  const standardDays = getLeaveDurationDays(activityType, birthType);
  const dates = calculateLeaveDates(startDate, standardDays, actualBirthDate);
  const TGS = dates ? dates.totalDays : standardDays;

  // 2. Əvvəlki 4 rüb (faktiki ÜTG)
  const quarterInfo = getPrecedingFourQuarters(startDate);
  const ÜTG = quarterInfo.totalCalendarDays || 365;

  // 3. Çıxılan təqvim günləri (ÇTG)
  const excludedInfo = calculateExcludedDays(
    excludedPeriods,
    quarterInfo.basePeriodStart,
    quarterInfo.basePeriodEnd
  );
  const ÇTG = excludedInfo.excludedDays;

  // 4. Bölən təqvim günləri (BTG = ÜTG - ÇTG)
  const BTG = Math.max(1, ÜTG - ÇTG);

  // 5. Ümumi qazanc (ÜQM)
  let totalMDSS = 0;
  quarterInfo.quarters.forEach((q) => {
    const val = parseFloat(quarterlyMDSS[q.key]) || 0;
    totalMDSS += val;
  });

  let ÜQM = 0;
  let OQM = 0;
  let isSpecialClause2_9 = false;

  if (hadEmployment) {
    // Qaydanın 2.7-ci bəndi: ÜQM = nəzərə alınan MDSS × 4
    ÜQM = totalMDSS * 4;
    OQM = ÜQM / BTG;
  } else {
    // Qaydanın 2.9-cu bəndi: Əvvəlki 4 rübdə əmək fəaliyyəti olmadıqda
    isSpecialClause2_9 = true;
    const sDate = new Date(startDate);
    const daysInStartMonth = new Date(sDate.getFullYear(), sDate.getMonth() + 1, 0).getDate();
    OQM = MIN_SALARY_2026 / daysInStartMonth;
    ÜQM = MIN_SALARY_2026;
  }

  // 6. Müavinət məbləği (MM = TGS × OQM)
  // Hüquqi tələb: Sün'i gündəlik kəsinti tətbiq olunmur, finalBenefit = rawBenefit
  const rawBenefit = TGS * OQM;
  const finalBenefit = rawBenefit;

  // 7. Aylıq maksimum hədd meyarı (Bənd 3.3 üzrə xəbərdarlıq indikatoru)
  const monthlyAverage = OQM * (ÜTG / 12);
  const isCapNoticeApplicable = monthlyAverage > MAX_MONTHLY_LIMIT_2026;

  // 8. Staj şərti (6 ay - bloklama deyil, xəbərdarlıq)
  const isEligibleSeniority = Number(seniorityMonths) >= 6;

  return {
    startDate: dates ? dates.startDate : startDate,
    endDate: dates ? dates.endDate : '',
    standardDays,
    delayDays: dates ? dates.delayDays : 0,
    TGS,
    activityType,
    birthType,
    seniorityMonths: Number(seniorityMonths),
    isEligibleSeniority,
    quarterInfo,
    totalMDSS,
    ÜQM,
    ÜTG,
    ÇTG,
    BTG,
    OQM,
    isSpecialClause2_9,
    rawBenefit,
    finalBenefit,
    monthlyAverage,
    isCapNoticeApplicable,
    maxMonthlyLimit: MAX_MONTHLY_LIMIT_2026,
    excludedInfo,
  };
}
